import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function Commission() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [edits, setEdits] = useState({});
  const canEdit = ["admin", "super_admin"].includes(user?.role);

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/commissions"), api.get("/doctors")]);
    setRows(r1.data);
    setDoctors(r2.data);
  };
  useEffect(() => { load(); }, []);

  const setE = (id, k, v) => setEdits((s) => ({ ...s, [id]: { ...(s[id] || {}), [k]: parseFloat(v) || 0 } }));

  const saveOne = async (doc) => {
    const patch = edits[doc.id];
    if (!patch) return;
    await api.put(`/doctors/${doc.id}/commission`, patch);
    toast.success(`${doc.name} commission updated`);
    setEdits((s) => { const x = { ...s }; delete x[doc.id]; return x; });
    load();
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-semibold">Doctor Commission</h1>
        <p className="text-sm text-slate-500">{canEdit ? "Edit commission percentages inline and save per doctor." : "Read-only view."}</p>
      </div>

      <div className="bg-white border">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny text-center">OPD %</th><th className="label-tiny text-center">IPD %</th><th className="label-tiny text-center">OT %</th><th className="label-tiny text-right">Gross</th><th className="label-tiny text-right">Doctor Share</th><th className="label-tiny text-right">Hospital</th><th></th></tr></thead>
          <tbody>
            {doctors.map((doc) => {
              const stat = rows.find((r) => r.doctor === doc.name) || { gross: 0, share: 0, hospital: 0 };
              const cur = edits[doc.id] || {};
              const changed = Object.keys(cur).length > 0;
              return (
                <tr key={doc.id} className="border-b">
                  <td className="p-2 font-medium">{doc.name}</td>
                  <td className="text-xs">{doc.department}</td>
                  <td className="text-center">
                    {canEdit ? <Input type="number" data-testid={`comm-opd-${doc.id}`} defaultValue={doc.opd_commission} onChange={(e) => setE(doc.id, "opd_commission", e.target.value)} className="w-16 h-8 text-center font-mono text-xs mx-auto" /> : <span className="font-mono">{doc.opd_commission}%</span>}
                  </td>
                  <td className="text-center">
                    {canEdit ? <Input type="number" defaultValue={doc.ipd_commission} onChange={(e) => setE(doc.id, "ipd_commission", e.target.value)} className="w-16 h-8 text-center font-mono text-xs mx-auto" /> : <span className="font-mono">{doc.ipd_commission}%</span>}
                  </td>
                  <td className="text-center">
                    {canEdit ? <Input type="number" defaultValue={doc.ot_commission} onChange={(e) => setE(doc.id, "ot_commission", e.target.value)} className="w-16 h-8 text-center font-mono text-xs mx-auto" /> : <span className="font-mono">{doc.ot_commission}%</span>}
                  </td>
                  <td className="text-right font-mono">{fmtINR(stat.gross)}</td>
                  <td className="text-right font-mono text-teal-700">{fmtINR(stat.share)}</td>
                  <td className="text-right font-mono">{fmtINR(stat.hospital)}</td>
                  <td>{canEdit && changed && <Button size="sm" onClick={() => saveOne(doc)} data-testid={`save-comm-${doc.id}`} className="h-7 rounded-sm bg-blue-700 hover:bg-blue-800"><Save className="w-3 h-3 mr-1"/>Save</Button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
